import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Grid,
  Group,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Divider,
} from '@mantine/core';
import {
  AlertTriangle,
  Crosshair,
  Layers,
  PlayCircle,
  Rocket,
  Server,
  Target,
} from 'lucide-react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Badge,
  Button,
  Card,
  PageContainer,
  PageHeader,
  Pill,
  Skeleton,
  Table,
  type TableColumn,
} from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { useScenario } from '../hooks/useScenarios';
import { DeployRangeModal } from '../components/scenarios/DeployRangeModal';
import { TopologyPreview } from '../components/scenario/ScenarioDetailPanel';
import { alpha, colors } from '../theme/tokens';
import type { Difficulty, ScenarioVm } from '../api/types';

const DIFFICULTY_VARIANT: Record<
  Difficulty,
  'success' | 'info' | 'warn' | 'danger'
> = {
  beginner: 'success',
  intermediate: 'info',
  advanced: 'warn',
  expert: 'danger',
};

export const Route = createFileRoute('/scenarios/$slug')({
  component: ScenarioDetail,
});

function ScenarioDetail() {
  const { slug } = Route.useParams();
  const { data, isPending, isError, error } = useScenario(slug);
  const [deployOpen, setDeployOpen] = useState(false);
  const navigate = useNavigate();

  if (isPending) {
    return (
      <PageContainer size="wide">
        <Stack gap="lg">
          <Skeleton height={36} width="40%" />
          <Skeleton height={18} width="70%" />
          <Skeleton height={120} radius="md" />
        </Stack>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer size="wide">
        <Alert
          icon={<AlertTriangle size={16} />}
          color="red"
          variant="light"
          title="Could not load scenario"
        >
          {(error as Error)?.message ?? 'Scenario not found'}
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Target}
        title={
          <Group gap="sm" align="center">
            <span>{data.name}</span>
            <Badge variant={DIFFICULTY_VARIANT[data.difficulty]}>{data.difficulty}</Badge>
            <Badge variant="neutral">v{data.version}</Badge>
          </Group>
        }
        description="Scenario overview, topology and deploy action."
        breadcrumbs={[
          { label: 'Forge', to: '/' },
          { label: 'Scenarios', to: '/scenarios' },
          { label: 'Detail' },
        ]}
        actions={
          <Button
            variant="primary"
            leftIcon={<Rocket size={14} />}
            onClick={() => setDeployOpen(true)}
          >
            Deploy range
          </Button>
        }
      />

      <motion.div variants={staggerContainer} initial="hidden" animate="show">
        <Grid gutter="md">
          <Grid.Col span={{ base: 6, md: 3 }}>
            <motion.div variants={cardItem}>
              <Metric icon={<Server size={16} />} label="VMs" value={String(data.vmCount)} />
            </motion.div>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <motion.div variants={cardItem}>
              <Metric icon={<Layers size={16} />} label="Phases" value={String(data.phases.length)} />
            </motion.div>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <motion.div variants={cardItem}>
              <Metric
                icon={<Crosshair size={16} />}
                label="Objectives"
                value={String(data.objectives.length)}
              />
            </motion.div>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 3 }}>
            <motion.div variants={cardItem}>
              <Metric
                icon={<PlayCircle size={16} />}
                label="$/hour"
                value={`$${data.costPerHour.toFixed(2)}`}
              />
            </motion.div>
          </Grid.Col>
        </Grid>
      </motion.div>

      <Tabs defaultValue="overview" variant="default">
        <Tabs.List>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="topology">Topology</Tabs.Tab>
          <Tabs.Tab value="phases">Attack Phases</Tabs.Tab>
          <Tabs.Tab value="objectives">Objectives</Tabs.Tab>
          <Tabs.Tab value="mitre">MITRE ATT&CK</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <OverviewPanel description={data.description} domain={data.domain} duration={data.durationMins} />
        </Tabs.Panel>
        <Tabs.Panel value="topology" pt="md">
          <TopologyPanel vms={data.vms} />
        </Tabs.Panel>
        <Tabs.Panel value="phases" pt="md">
          <PhasesPanel phases={data.phases} />
        </Tabs.Panel>
        <Tabs.Panel value="objectives" pt="md">
          <ObjectivesPanel objectives={data.objectives} />
        </Tabs.Panel>
        <Tabs.Panel value="mitre" pt="md">
          <MitrePanel techniques={data.techniques} />
        </Tabs.Panel>
      </Tabs>

      {/* DeployScenarioModal (legacy single-tenant flow) replaced 2026-05-25
          with the team-mode DeployRangeModal that exposes a cohort + trainee
          multi-select. The old component is kept below for back-compat if any
          other route still uses it - this route now uses the new modal. */}
      <DeployRangeModal
        opened={deployOpen}
        onClose={() => setDeployOpen(false)}
        scenario={data}
        onDeployed={(rangeId) => {
          // F1: hand the operator straight to the live range page so the
          // ~10-min provisioning wait has a destination (status, per-VM health,
          // consoles-on-ready) instead of dead-ending on the scenario page.
          navigate({ to: '/ranges/$id', params: { id: rangeId } });
        }}
      />
    </PageContainer>
  );
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Card>
      <Stack gap={6}>
        <Group gap={6}>
          <span style={{ color: '#22d3ee' }}>{icon}</span>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {label}
          </Text>
        </Group>
        <Text ff="JetBrains Mono" fw={700} size="24px" c="rgb(var(--c-txt-bright))">
          {value}
        </Text>
      </Stack>
    </Card>
  );
}

function OverviewPanel({
  description,
  domain,
  duration,
}: {
  description: string;
  domain: string;
  duration: number;
}) {
  return (
    <Card>
      <Stack gap="sm">
        <Group gap="sm">
          <Badge variant="info">{domain}</Badge>
          <Badge variant="neutral">{duration} min target</Badge>
        </Group>
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
          {description}
        </Text>
      </Stack>
    </Card>
  );
}

function TopologyPanel({ vms }: { vms: ReadonlyArray<ScenarioVm> }) {
  const columns: ReadonlyArray<TableColumn<ScenarioVm>> = [
    {
      key: 'hostname',
      header: 'Hostname',
      cell: (vm) => (
        <Text ff="JetBrains Mono" size="sm" c="rgb(var(--c-txt-bright))">
          {vm.hostname}
        </Text>
      ),
    },
    { key: 'role', header: 'Role', cell: (vm) => <Text size="sm">{vm.role}</Text> },
    {
      key: 'ami',
      header: 'AMI',
      cell: (vm) => (
        <Text size="sm" ff="JetBrains Mono" c="dimmed">
          {vm.amiKey}
        </Text>
      ),
    },
    { key: 'instance', header: 'Instance', cell: (vm) => <Text size="sm">{vm.instanceType}</Text> },
    {
      key: 'disk',
      header: 'Disk',
      align: 'right',
      cell: (vm) => <Text size="sm">{vm.diskGb} GB</Text>,
    },
  ];

  return (
    <Stack gap="md">
      {/* The scenario blueprint topology — segment columns (attacker / external /
          DMZ / internal) with animated attack, DNAT and telemetry edges. */}
      <TopologyPreview vms={vms} />
      <Table columns={columns} rows={vms} rowKey={(vm) => vm.hostname} />
    </Stack>
  );
}

function PhasesPanel({
  phases,
}: {
  phases: ReadonlyArray<{ id: string; name: string; description: string }>;
}) {
  return (
    <Stack gap="sm">
      {phases.map((phase, i) => (
        <Card key={phase.id}>
          <Group gap="md" align="start">
            <Group
              gap={4}
              wrap="nowrap"
              style={{
                minWidth: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: alpha('neon', 0.13),
                border: `1px solid ${colors.neon}`,
                justifyContent: 'center',
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 12,
                color: colors.neon,
                fontWeight: 700,
              }}
            >
              {i + 1}
            </Group>
            <Stack gap={4}>
              <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">
                {phase.name}
              </Text>
              <Text size="xs" c="dimmed">
                {phase.description}
              </Text>
            </Stack>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

function ObjectivesPanel({
  objectives,
}: {
  objectives: ReadonlyArray<{
    id: string;
    title: string;
    description: string;
    points: number;
    detectionIds: ReadonlyArray<string>;
  }>;
}) {
  return (
    <ScrollArea>
      <Stack gap="sm">
        {objectives.map((obj) => (
          <Card key={obj.id}>
            <Stack gap={6}>
              <Group justify="space-between">
                <Text fw={600} size="sm">
                  {obj.title}
                </Text>
                <Badge variant="info">{obj.points} pts</Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {obj.description}
              </Text>
              <Group gap={4}>
                {obj.detectionIds.map((id) => (
                  <Pill key={id} size="xs" variant="neutral">
                    {id}
                  </Pill>
                ))}
              </Group>
            </Stack>
          </Card>
        ))}
      </Stack>
    </ScrollArea>
  );
}

function MitrePanel({ techniques }: { techniques: ReadonlyArray<string> }) {
  return (
    <Card>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Techniques exercised by this scenario. Click into the analytics tab on a deployed range to
          see which techniques fired live.
        </Text>
        <Divider color="var(--c-bd)" />
        <Group gap={6}>
          {techniques.map((t) => (
            <Pill key={t} size="md" variant="warn">
              {t}
            </Pill>
          ))}
        </Group>
      </Stack>
    </Card>
  );
}

